import { PartitionModel } from './DataSourceModel';
import { SelectOptions } from 'core-ui/MultipleSelectSortable/MultipleSelectSortable';
import { AdvancedFilterFieldConfig } from 'models/WidgetModel';

/**
 * A single option shown in the Advanced Settings multi-select.
 */
export interface PercentageValueOption {
  /** Rating number (for rating fields), string id (for non-rating fields), or boolean (for SimpleBoolean fields) */
  value: number | string | boolean;
  /** Display label shown in the multi-select */
  label: string;
  /** Optional path to indicate whether this is a rating-based option, an id-based option, or a keyword option */
  path?: string;
}

/**
 * Stores the user's selections from the Advanced Settings view for Percentage / NPS fields.
 * Defaults are derived from the field's Responses when the calculation type is first selected;
 * the user can then override them in CalculationsSettingsManager.
 */
export interface PercentageSettingsModel {
  /** Values to use in ValueCount.Values (ratings) or as ids (non-rating fields) */
  selectedValues: PercentageValueOption[];
  /** Path override — field.Path for rating fields, 'id' for non-rating fields */
  path: string;
}

/**
 * Stores NPS-specific settings for customizing detractors, neutrals, and promoters ranges.
 * Allows users to adjust the boundaries between these categories via the Advanced Settings modal.
 */
export interface NpsSettingsModel {
  /** The upper bound of the Detractors range (e.g., 6 means detractors are 0-6) */
  detractorsMax: number;
  /** The lower bound of the Promoters range (e.g., 9 means promoters are 9-10) */
  promotersMin: number;
  /** Path override — field.Path for rating fields */
  path: string;
}

/**
 * Comparison applied to ValueCount.Values for numeric fields (SimpleInt, SimpleDouble, IntegerQuestion, DoubleQuestion).
 * 'equals' is the default and is omitted from the payload; 'between'/'outside' take two values (lower, upper).
 */
export type NumericValueOperator =
  | 'equals'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'between'
  | 'outside';

/**
 * Advanced settings for Percentage / Frequency on numeric fields.
 */
export interface NumericSettingsModel {
  valueOperator: NumericValueOperator;
  /** One value for single-value operators, [lower, upper] for 'between'/'outside' */
  values: number[];
}

/**
 * Logical operator used to combine multiple filters.
 * AND = all conditions must match; OR = any condition can match.
 */
export type FilterLogicalOperator = 'AND' | 'OR';

/**
 * Represents a single advanced filter configured by the user.
 */
export interface FieldFilter {
  /** Unique identifier for this filter instance */
  id: string;
  /** GUID of the selected field/question to filter on */
  filterGuid: string;
  /** Display name of the selected field/question */
  filterName: string;
  /** TypeName of the selected field (e.g. "RatingQuestion", "SingleChoice", "SimpleBoolean") */
  fieldTypeName: string;
  /** Resolved attribute path for the API (e.g. "website_segment.id") */
  attributePath: string;
  /** Attribute kind for the API: "Collection" for multi-choice types, "Scalar" otherwise */
  attributeKind: 'Scalar' | 'Collection';
  /** SubAttribute of the field (e.g., "Text", "Response", "Numeric Value") */
  subAttribute: string;
  /** Selected operator (e.g. "In", "EqualTo") */
  operator: string;
  /** Selected response values (multi-select) */
  selectedValues: SelectOptions[];
  /** GUID of the selected filter value — kept for interface compatibility */
  selectedValueGuid: string;
  /** Display label — kept for interface compatibility */
  selectedValueLabel: string;
  /** Operator joining this filter to the preceding one in the list; ignored for the first filter */
  logicalOperator?: FilterLogicalOperator;
}

/**
 * Represents a single field entry in the FieldsManager form.
 * Each field contains user-configurable properties for data visualization and calculation.
 */
export interface SelectedFieldModel {
  /** Sequential number identifying the field's position */
  fieldNumber: number;
  /** Reference to the selected field from available options */
  field: FieldModel;
  /** User-defined display name shown in the UI */
  displayName: string;
  /** Selected calculation method to apply to the field data */
  calculationType: CalculationOptionModel | null;
  /** Number format precision (e.g., "0.000" for three decimal places) */
  precision: string;
  /** Whether to normalize the field values */
  normalize: boolean;
  /** Whether to apply statistical testing to the field */
  statTesting: boolean;
  /** Whether this field should be rendered as L1 (left-aligned, bold, highlighted row) or L2 (indented, no bold, no highlight).
   * Maps to Metadata.rowType: "L1" or "L2".
   * Toggle visible only for Percentage (user can change). Other types: Mean=true (hidden), NPS=false (hidden), TotalCount=true (hidden). */
  isL1Category?: boolean;
  /** Advanced settings for Percentage calculation type (populated with defaults when calc type is chosen) */
  percentageSettings?: PercentageSettingsModel;
  /** Advanced settings for NPS calculation type (populated with defaults when calc type is chosen) */
  npsSettings?: NpsSettingsModel;
  /** Advanced settings for Percentage / Frequency on numeric fields */
  numericSettings?: NumericSettingsModel;
  /** When true, this is the pinned "Project Frequency" field: always at the top of the list,
   * cannot be removed or reordered, and only its Display Name is user-configurable.
   * Its calculation config is supplied automatically at serialization time. */
  isProjectFrequency?: boolean;
  /** Advanced filters applied to this field (persisted across navigation) */
  filters?: FieldFilter[];
  /** @deprecated superseded by per-filter FieldFilter.logicalOperator; kept only to read legacy saved widgets */
  filterLogicalOperator?: FilterLogicalOperator;
}

/**
 * Collection of selected fields for a specific partition.
 * Groups all field configurations associated with a data partition.
 */
export interface SelectedUserFields {
  /** Guid of the parent partition */
  partitionGuid: string;
  /** Array of configured field entries */
  fields: SelectedFieldModel[];
  /** Indicates if this object comes from edit mode */
  fromEditMode?: boolean;
  /** Advanced filters applied at the partition level (independent of per-field filters) */
  partitionFilters?: FieldFilter[];
  /** @deprecated superseded by per-filter FieldFilter.logicalOperator; kept only to read legacy saved widgets */
  partitionFiltersLogicalOperator?: FilterLogicalOperator;
}

/**
 * Represents an option value for a field, with optional Flag indicators
 */
export interface FieldOptionModel {
  /** Value of the option */
  value: any;
  /** Display label for the option */
  label?: string;
  /** Flag indicator (e.g., "DK", "NA", "REF" for special values) */
  flag?: string;
}

/**
 * Core field model representing a selectable data field.
 * Contains the unique identifier, name, and optional field options with flags.
 */
export interface FieldModel {
  /** Unique identifier for the field */
  fieldGuid: string;
  /** Display name of the field */
  fieldName: string;
  /** Short display text from the API response */
  displayName?: string;
  /** Optional array of field options containing Flag values */
  options?: FieldOptionModel[];
  /** Additional raw properties from the API response */
  [key: string]: any;
}

/**
 * Represents a calculation method that can be applied to field data.
 * Defines the type, display name, and associated field for calculations.
 */
export interface CalculationOptionModel {
  /** Calculation type identifier (e.g., "Sum", "Average", "Count") */
  type: string;
  /** User-friendly name displayed in the UI */
  displayName: string;
  /** Name of the field this calculation applies to */
  fieldName: string;
  totalMetric?: any;
  /** Optional ValueCount operation details for count-based calculations */
  valueCount?: ValueCountOperationModel;
}

/**
 * Represents the count values operation
 */
export interface ValueCountOperationModel {
  /** Type of the value count operation */
  type: string;
  /** Array of values involved in the operation - supports numbers, strings (for flags like DK/NA/REF), and booleans (for SimpleBoolean fields) */
  values: (number | string | boolean)[];
  fieldName?: string;
  /** '' for numeric fields */
  path?: string;
  mixedValueMatchMode?: string;
  /** Set for numeric fields when the operator is anything other than 'equals' */
  valueOperator?: Exclude<NumericValueOperator, 'equals'>;
}

/**
 * Represents a selectable filter dimension (e.g. "Region", "Quarter").
 * Each filter has a unique identifier, a display name, and a list of allowed values.
 */
export interface FilterOptionModel {
  /** Unique identifier for the filter dimension */
  filterGuid: string;
  /** Display name shown in the UI (e.g. "Region") */
  filterName: string;
  /** Allowed values the user can pick from for this filter */
  values: FilterValueModel[];
}

/**
 * A single selectable value within a filter dimension.
 */
export interface FilterValueModel {
  /** Unique identifier for the value */
  valueGuid: string;
  /** Human-readable label (e.g. "North East", "Q1 2025") */
  label: string;
}

/**
 * A user-configured filter applied to a field.
 * Pairs a selected filter dimension with a chosen value.
 */
export interface SelectedFilterModel {
  /** Reference to the filter dimension */
  filter: FilterOptionModel;
  /** The value chosen by the user */
  selectedValue: FilterValueModel;
}

/**
 * Available configuration options for the FieldsManager component.
 * Provides the parent partition context and available selections for fields and calculations.
 */
export interface FieldsManagerViewOptionsModel {
  /** The parent partition this field manager is associated with */
  parentPartition: PartitionModel;
  /** Available calculation types the user can select — shape varies per endpoint, only DisplayName is guaranteed */
  CalculationTypeOptions: any[];
  /** Available fields the user can select from */
  FieldsOptions: FieldModel[];
  /** Available filter fields from Config.Filters.AdvancedFilters */
  FiltersOptions: AdvancedFilterFieldConfig[];
}

/**
 * Defines the visibility and enabled state for each form control in a field entry.
 * Controls conditional rendering and interaction based on user selections.
 *
 * Default states:
 * - SelectField: Visible and enabled
 * - DisplayName: Visible and enabled
 * - CalculationType: Visible but disabled until field is selected
 * - Precision: Visible and enabled
 * - Normalize: Hidden and disabled (shown when field has flagged responses)
 * - StatTesting: Hidden and disabled (shown for Mean, Percentage, NPS)
 * - isL1Category: Hidden and disabled (shown ONLY for Percentage; all other types are hidden with forced values)
 */
export interface FieldStateModel {
  /** State for the field selector dropdown */
  SelectField: FormItemState;
  /** State for the display name input field */
  DisplayName: FormItemState;
  /** State for the calculation type selector (disabled by default) */
  CalculationType: FormItemState;
  /** State for the precision format input */
  Precision: FormItemState;
  /** State for the normalize checkbox (invisible and disabled by default) */
  Normalize: FormItemState;
  /** State for the statistical testing checkbox (invisible and disabled by default) */
  StatTesting: FormItemState;
  /** State for the highlight checkbox (visible only for Frequency calculation type) */
  isL1Category: FormItemState;
  /** State for the advanced settings link (visible only for Percentage and NPS calculation types) */
  AdvancedSettings: FormItemState;
  /** State for the filters tab (visible and enabled by default) */
  Filters: FormItemState;
}

/**
 * Represents the visibility and interaction state of a form control.
 */
export interface FormItemState {
  /** Whether the form control should be rendered in the UI */
  visible: boolean;
  /** Whether the form control is disabled (does not accept user interaction) */
  disabled: boolean;
}

/**
 * Default field state configuration.
 * Normalize and StatTesting are hidden until a calculation type is selected.
 */
export const DEFAULT_FIELD_STATE: FieldStateModel = {
  SelectField: { visible: true, disabled: false },
  DisplayName: { visible: true, disabled: false },
  CalculationType: { visible: true, disabled: true },
  Precision: { visible: true, disabled: false },
  Normalize: { visible: false, disabled: true },
  StatTesting: { visible: false, disabled: true },
  isL1Category: { visible: false, disabled: true },
  AdvancedSettings: { visible: false, disabled: true },
  Filters: { visible: false, disabled: true },
};

/**
 * Defines the visibility and enabled state for each form control in a filter entry.
 * Controls conditional rendering and interaction based on user selections.
 *
 * Mirrors FieldStateModel but for filter-specific controls:
 * - FieldSelect: The field/question picker
 * - Operator: The condition operator dropdown ("Any of these", "Is exactly")
 * - Values: The multi-select for response values
 * - RemoveButton: The trash icon to delete the filter
 *
 * Default states:
 * - FieldSelect: Visible and enabled
 * - Operator: Hidden and disabled (shown after field is selected)
 * - Values: Hidden and disabled (shown after field is selected)
 * - RemoveButton: Visible and enabled
 */
export interface FilterStateModel {
  /** State for the field/question selector dropdown */
  FieldSelect: FormItemState;
  /** State for the operator/condition dropdown (disabled until a field is selected) */
  Operator: FormItemState;
  /** State for the multi-select response values (disabled until a field is selected) */
  Values: FormItemState;
  /** State for the remove/trash button */
  RemoveButton: FormItemState;
}

/**
 * Default filter state configuration.
 * Operator and Values are hidden until a field/question is selected.
 */
export const DEFAULT_FILTER_STATE: FilterStateModel = {
  FieldSelect: { visible: true, disabled: false },
  Operator: { visible: false, disabled: true },
  Values: { visible: false, disabled: true },
  RemoveButton: { visible: true, disabled: false },
};
